// src/pages/billing/UsageCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useBillingUsage, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

export const UsageCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useBillingUsage();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [formData, setFormData] = useState({
    billing_account_id: '',
    usage_type: '',
    quantity: '',
    unit: '',
    action: 'increment',
    idempotency_key: '',
  });
  const [batchRecords, setBatchRecords] = useState([{ usage_type: '', quantity: '', unit: '' }]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBatchChange = (index: number, field: string, value: string) => {
    setBatchRecords((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addBatchRecord = () => {
    setBatchRecords((prev) => [...prev, { usage_type: '', quantity: '', unit: '' }]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (mode === 'single') {
        await store.CreateUsageRecord({
          billing_account: { id: formData.billing_account_id },
          usage_type: formData.usage_type,
          quantity: formData.quantity,
          unit: formData.unit,
          action: formData.action,
          idempotency_key: formData.idempotency_key || undefined,
        } as any);
      } else {
        await store.CreateUsageRecordBatch({
          records: {
            records: batchRecords.map((r) => ({
              billing_account: { id: formData.billing_account_id },
              usage_type: r.usage_type,
              quantity: r.quantity,
              unit: r.unit,
            })),
          },
        } as any);
      }
      navigate('/billing/usage');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Record Usage</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/usage" className="text-muted-foreground hover:text-foreground">Usage</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Record Usage</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <div className="flex gap-2">
        <Button variant={mode === 'single' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('single')}>Single Record</Button>
        <Button variant={mode === 'batch' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('batch')}>Batch</Button>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{mode === 'single' ? 'Usage Record' : 'Batch Usage Records'}</CardTitle>
            <CardDescription>{mode === 'single' ? 'Record a single usage event' : 'Record multiple usage events at once'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input label="Billing Account ID" type="text" value={formData.billing_account_id} onChange={(e) => handleChange('billing_account_id', e.target.value)} placeholder="Billing account ID" required />

            {mode === 'single' ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <Input label="Usage Type" type="text" value={formData.usage_type} onChange={(e) => handleChange('usage_type', e.target.value)} placeholder="api_calls" required />
                  <Input label="Quantity" type="text" value={formData.quantity} onChange={(e) => handleChange('quantity', e.target.value)} placeholder="100" required />
                  <Input label="Unit" type="text" value={formData.unit} onChange={(e) => handleChange('unit', e.target.value)} placeholder="calls" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Action</label>
                    <Select
                      value={formData.action}
                      onChange={(e) => handleChange('action', e.target.value)} className="w-full">
                      <option value="increment">Increment</option>
                      <option value="set">Set</option>
                    </Select>
                  </div>
                  <Input label="Idempotency Key (optional)" type="text" value={formData.idempotency_key} onChange={(e) => handleChange('idempotency_key', e.target.value)} placeholder="Unique key" />
                </div>
              </>
            ) : (
              <>
                {batchRecords.map((record, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1"><Input label={index === 0 ? 'Usage Type' : undefined} type="text" value={record.usage_type} onChange={(e) => handleBatchChange(index, 'usage_type', e.target.value)} placeholder="api_calls" required /></div>
                    <div className="w-28"><Input label={index === 0 ? 'Quantity' : undefined} type="text" value={record.quantity} onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)} placeholder="100" required /></div>
                    <div className="w-28"><Input label={index === 0 ? 'Unit' : undefined} type="text" value={record.unit} onChange={(e) => handleBatchChange(index, 'unit', e.target.value)} placeholder="calls" /></div>
                    {batchRecords.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => setBatchRecords((p) => p.filter((_, i) => i !== index))}>Remove</Button>}
                  </div>
                ))}
                <Button type="button" variant="secondary" onClick={addBatchRecord}>Add Record</Button>
              </>
            )}
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to="/billing/usage"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.creating') : 'Record Usage'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default UsageCreatePage;
